'use client';
import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { useT } from '../lib/i18n';

/**
 * 토스트 — **디자이너 시안(alertguide.html) 그대로**(§16, 2026-08-28 사용자 지시로 실물 반영).
 *
 * 담기·찜처럼 **알리기만** 하는 반응이다. 화면을 막지 않고 아래 가운데에 잠깐 떴다 사라진다
 * (`alert()` 는 2026-08-22 에 폐지했다). 되묻거나 읽고 넘어가야 하는 것은 알림창(`Alert.tsx`)이다.
 *
 * 쓰기 — 어디서든 `toast(t('prd.addedToCart'))`. 층간 전달(props) 없이 이벤트로 받는다.
 * `<ToastHost />` 는 레이아웃에 한 번만 둔다.
 *
 * **시안은 `.show` 가 붙어야 보인다** — `.toast` 기본이 `opacity: 0` 이다. 붙는 시점을 한 틱
 * 늦춰야 떠오르는 것이 보인다. 전에는 우리 모양(검은 알약)을 쓰면서 `.show` 를 안 붙였는데,
 * 시안이 `.toast` 를 정의한 뒤로 **토스트가 투명하게 떴다 사라지고 있었다**(2026-08-28 발견).
 */
const EVENT = 'app:toast';

/** 시안 TOAST_ICONS 와 같은 짝 */
export type ToastKind = 'info' | 'success' | 'warning' | 'danger';

/**
 * 두 글자(`ok`·`error`)로 부르던 자리를 그대로 살린다 — 부르는 곳이 쉰 곳이 넘는다.
 * 새로 쓸 때는 시안 이름(`info`·`success`·`warning`·`danger`)을 쓰면 된다.
 */
type ToastArg = ToastKind | 'ok' | 'error';
const NORMALIZE: Record<ToastArg, ToastKind> = {
  ok: 'success', error: 'danger',
  info: 'info', success: 'success', warning: 'warning', danger: 'danger',
};

export function toast(message: string, kind: ToastArg = 'ok') {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { message, kind: NORMALIZE[kind] } }));
}

interface Item { id: number; message: string; kind: ToastKind; show: boolean }

/** 아이콘 이름은 태그 밖 상수로 — check-icons 가 문자열을 전수 검사한다 */
const ICON_INFO = 'info';
const ICON_SUCCESS = 'check';
const ICON_WARNING = 'triangle-alert';
const ICON_DANGER = 'circle-alert';
const ICON_CLOSE = 'x';
const ICON: Record<ToastKind, string> = {
  info: ICON_INFO, success: ICON_SUCCESS, warning: ICON_WARNING, danger: ICON_DANGER,
};

/** 시안 기본값(showToast duration) */
const TTL = 3000;

let seq = 0;

export function ToastHost() {
  const t = useT();
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    const on = (e: Event) => {
      const { message, kind } = (e as CustomEvent<{ message: string; kind: ToastKind }>).detail;
      const id = ++seq;
      setItems((cur) => [...cur, { id, message, kind, show: false }]);
      // `.show` 는 한 틱 뒤에 붙인다 — 같은 프레임에 붙이면 떠오르는 전이가 보이지 않는다
      requestAnimationFrame(() => setItems((cur) => cur.map((x) => (x.id === id ? { ...x, show: true } : x))));
      setTimeout(() => setItems((cur) => cur.filter((x) => x.id !== id)), TTL);
    };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);

  if (!items.length) return null;
  return (
    <div className="toast-container" role="status" aria-live="polite">
      {items.map((x) => (
        <div key={x.id} className={`toast${x.show ? ' show' : ''}`}>
          <span className={`toast-icon ${x.kind}`}><Icon name={ICON[x.kind]} /></span>
          <span className="toast-msg">{x.message}</span>
          <button className="toast-close" type="button" aria-label={t('action.close')}
            onClick={() => setItems((cur) => cur.filter((i) => i.id !== x.id))}>
            <Icon name={ICON_CLOSE} />
          </button>
        </div>
      ))}
    </div>
  );
}
